"use client";
import type { AuthState } from "@jetblue/kit-auth";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { DotcomAuthContext } from "../context";
import { useFetchProfile } from "./hooks/use-fetch-profile";
import type { DotcomAuthSession, KitAuthConfig } from "../types";
import { useKitAuth } from "./hooks/use-kit-auth";

export type KitAuthSessionProperties = {
  authConfig: KitAuthConfig;
  children: ReactNode;
};
export const KitAuthSession = ({
  authConfig,
  children,
}: KitAuthSessionProperties) => {
  const {
    isAuthenticated,
    accessToken,
    error,
    isLoading: isLoadingKitAuth,
    handleLogout,
    handleLogin,
  } = useKitAuth();

  const {
    data: profile,
    error: profileError,
    isLoading: isLoadingProfile,
  } = useFetchProfile({
    accessToken,
    enabled: Boolean(accessToken),
    url: authConfig.PROFILE_URL,
  });

  const value = useMemo<DotcomAuthSession>(() => {
    const combinedError = error ?? profileError ?? null;

    let status: AuthState;
    if (combinedError) {
      status = "unauthenticated";
    } else if (isLoadingKitAuth || isLoadingProfile) {
      status = "authenticating";
    } else {
      status = isAuthenticated ? "authenticated" : "unauthenticated";
    }

    return {
      accessToken,
      customerDetails: profile,
      error: combinedError,
      handleLogin,
      handleLogout,
      status,
    };
  }, [
    accessToken,
    error,
    isAuthenticated,
    profile,
    profileError,
    isLoadingKitAuth,
    isLoadingProfile,
    handleLogin,
    handleLogout,
  ]);

  return (
    <DotcomAuthContext.Provider value={value}>
      {children}
    </DotcomAuthContext.Provider>
  );
};
